/* eslint-disable max-len */
import {
    useCallback,
    // useEffect,
    useMemo,
} from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Form,
    DatePicker,
    Select,
    Button,
    Space,
    Divider,
    Popover,
    Row,
    Col,
} from 'antd';

import IncrementNumberInput from '@/components/IncrementNumberInput';
import { CalendarV2Icon, DownIcon, GuestsIcon } from '@/assets/images/icons/screens';
import { dateFormat } from '@/helpers/dateFormat';
import { actions as accommodationActions } from '@/redux/accommodation/hotel/view/reducer';

const Filters = ({
    filterData,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const onSearch = useCallback((values) => {
        const roomFilterData = {
            adults: values.adults === undefined ? 2 : values.adults,
            children: values.children,
            date_start: values.date?.[0]?.format(dateFormat),
            date_end: values.date?.[1]?.format(dateFormat),
            ages: values.children !== 0
                ? Object.keys(values)
                    .filter((key) => key.startsWith('age'))
                    .map((key) => values[key])
                : [],
        };

        dispatch(accommodationActions.saveRoomFilterData(roomFilterData));
        dispatch(accommodationActions.sendRequest());
    }, [dispatch]);

    const initialData = useMemo(() => {
        if (filterData) {
            return {
                date: [
                    filterData.date_start ? moment(filterData.date_start) : null,
                    filterData.date_end ? moment(filterData.date_end) : null,
                ],
                adults: filterData.adults || 2,
                children: filterData.children || 0,
                ...filterData.ages.reduce((acc, age, index) => {
                    acc[`age${index}`] = age;
                    return acc;
                }, {}),
            };
        }
        return {};
    }, [filterData]);

    const setAdultNumber = useCallback((value) => {
        dispatch(accommodationActions.saveRoomFilterData({
            ...filterData,
            adults: value,
        }));
    }, [dispatch, filterData]);

    const setChildNumber = useCallback((value) => {
        if (value < filterData.children) {
            const lastIndex = filterData.children - 1;
            form.setFieldsValue({
                [`age${lastIndex}`]: undefined, // Only reset the last one
            });
            dispatch(accommodationActions.saveRoomFilterData({
                ...filterData,
                ages: filterData.ages.slice(0, value),
                children: value,
            }));
        } else {
            dispatch(accommodationActions.saveRoomFilterData({
                ...filterData,
                children: value,
            }));
        }
    }, [dispatch, filterData]);

    return (
        <Row className="hotel-reservation-search-form">
            <Col xl={15} lg={20} xs={24}>
                <Form
                    onFinish={onSearch}
                    form={form}
                    initialValues={initialData}
                >
                    <Row
                        gutter={[10, 10]}
                    >
                        <Col lg={9} xs={24}>
                            <Space size={0} className="date-picker-wrapper">
                                <CalendarV2Icon />
                                <Form.Item name="date" noStyle>
                                    <DatePicker.RangePicker
                                        popupClassName="filter-calendar"
                                        suffixIcon={null}
                                        onChange={(dates) => {
                                            dispatch(accommodationActions.saveRoomFilterData({
                                                ...filterData,
                                                date_start: dates?.[0]?.format(dateFormat),
                                                date_end: dates?.[1]?.format(dateFormat),
                                            }));
                                        }}
                                        getPopupContainer={(trigger) => {
                                            return trigger?.parentElement;
                                        }}
                                        separator="-"
                                        placeholder={[t('Check in'), t('Check out')]}
                                    />
                                </Form.Item>
                            </Space>
                        </Col>
                        <Col lg={9} xs={24}>
                            <div className="guests_filters">
                                <Popover
                                    trigger="click"
                                    placement="bottom"
                                    getPopupContainer={(trigger) => {
                                        return trigger?.parentElement;
                                    }}
                                    overlayClassName="reservation-guests_dropdown"
                                    content={(
                                        <div className="reservation-guests_dropdown_content">
                                            <div>
                                                <Space size={145}>
                                                    <span>{t('Adults')}</span>
                                                    <Form.Item name="adults" noStyle>
                                                        <IncrementNumberInput
                                                            hasInputBorder={false}
                                                            onChange={setAdultNumber}
                                                            defaultCount={filterData.adults}
                                                        />
                                                    </Form.Item>
                                                </Space>
                                            </div>
                                            <div>
                                                <Space size={130}>
                                                    <span>{t('Children')}</span>
                                                    <Form.Item name="children" noStyle>
                                                        <IncrementNumberInput
                                                            hasInputBorder={false}
                                                            onChange={setChildNumber}
                                                            defaultCount={filterData.children}
                                                        />
                                                    </Form.Item>
                                                </Space>
                                            </div>
                                            <>
                                                {filterData.children !== 0 && <Divider />}
                                                <div className="age-select-wrapper">
                                                    {new Array(filterData.children).fill(0).map((_, i) => (
                                                        <Form.Item
                                                            name={`age${i}`}
                                                            noStyle
                                                            key={i.toString()}
                                                        >
                                                            <Select
                                                                key={i.toString()}
                                                                placeholder={t('Age Needed')}
                                                                onChange={(value) => {
                                                                    const updatedAges = [...filterData.ages];
                                                                    updatedAges[i] = value;
                                                                    dispatch(accommodationActions.saveRoomFilterData({
                                                                        ...filterData,
                                                                        ages: updatedAges,
                                                                    }));
                                                                }}
                                                            >
                                                                {new Array(18).fill(0).map((__, j) => (
                                                                    <Select.Option
                                                                        key={j.toString()}
                                                                        value={j}
                                                                    >
                                                                        {j === 1 ? `${j} ${t('year old')}` : `${j} ${t('years old')}`}
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    ))}
                                                </div>
                                            </>
                                        </div>
                                    )}
                                >
                                    <Button block>
                                        <Space>
                                            <div>
                                                <GuestsIcon />
                                                {filterData.adults !== 0 || filterData.children !== 0 ? (
                                                    <div className="guests-options_picked">
                                                        {filterData.adults !== 0 && filterData.adults !== undefined && (
                                                            `${filterData.adults} ${(filterData.adults === 1)
                                                                ? (filterData.children !== 0 && filterData.children !== undefined) ? `${t('Adult')}, ` : `${t('Adult')}`
                                                                : (filterData.children !== 0 && filterData.children !== undefined) ? `${t('Adults')}, ` : `${t('Adults')}`}`
                                                        )}
                                                        {filterData.children !== 0 && filterData.children !== undefined && (
                                                            `${filterData.children} ${filterData.children === 1 ? ` ${t('Child')}` : ` ${t('Children')}`}`
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="btn-text">{t('Guests')}</div>
                                                )}
                                            </div>
                                            <DownIcon />
                                        </Space>
                                    </Button>
                                </Popover>
                            </div>
                        </Col>
                        <Col lg={6} xs={24}>
                            <Button
                                className="search-button"
                                type="text"
                                block
                                onClick={() => form.submit()}
                            >
                                {t('Check Availability')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
};

export default Filters;
